import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
    faShieldAlt, faSearch, faDownload, faEye, 
    faLock, faUser, faDatabase, faGlobe
} from '@fortawesome/free-solid-svg-icons';
import DashboardHeader from '../../layouts/dashboard_header';
import SideMenu from '../../layouts/dashboard_menu';
import DashboardFooter from '../../layouts/dashboard_footer';
import localForage from 'localforage';

interface PrivacySection {
    id: string;
    title: string;
    content: string;
    lastUpdated: string;
}

const PrivacyPolicy: React.FC = () => {
    const [sections, setSections] = useState<PrivacySection[]>([]);
    const [searchQuery, setSearchQuery] = useState<string>('');
    const [selectedSection, setSelectedSection] = useState<string>('overview');
    const [clinicName, setClinicName] = useState<string>('Facility');

    useEffect(() => {
        document.title = "Privacy Policy - Uzima Healthcare";
        loadClinicData();
        loadPrivacyPolicy();
    }, []);

    const loadClinicData = async () => {
        try {
            const storedClinicName = await localForage.getItem('clinic_name');
            if (storedClinicName) {
                setClinicName(storedClinicName as string);
            }
        } catch (error) {
            console.error('Error loading clinic data:', error);
        }
    };

    const loadPrivacyPolicy = () => {
        // Mock privacy policy data - in real app, fetch from API
        const mockSections: PrivacySection[] = [
            {
                id: 'overview',
                title: 'Overview',
                content: `Uzima Healthcare is committed to protecting your privacy and personal information. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our healthcare platform and services.

We understand that healthcare information is sensitive and requires the highest level of protection. This policy applies to all users of our platform, including patients, healthcare professionals, and institutional administrators.

By using our services, you agree to the collection and use of information in accordance with this policy. We will not use or share your information with anyone except as described in this Privacy Policy.`,
                lastUpdated: '2025-09-05'
            },
            {
                id: 'information-collection',
                title: 'Information We Collect',
                content: `We collect several types of information to provide and improve our services:

Personal Information:
- Name, email address, phone number, and contact information
- Date of birth, gender, and demographic information
- Medical history, health records, and treatment information
- Insurance information and billing details
- Authentication credentials and security information

Technical Information:
- Device information and IP addresses
- Browser type and version
- Operating system and platform information
- Usage patterns and interaction data
- Cookies and similar tracking technologies

Healthcare Data:
- Medical records and health information
- Treatment plans and medication history
- Telehealth session recordings and transcripts
- IoT device data and health monitoring information
- Laboratory results and diagnostic information`,
                lastUpdated: '2025-09-05'
            },
            {
                id: 'data-usage',
                title: 'How We Use Your Information',
                content: `We use the collected information for the following purposes:

Healthcare Services:
- Providing medical care and treatment services
- Facilitating telehealth consultations and sessions
- Managing patient records and health information
- Coordinating care between healthcare providers
- Processing insurance claims and billing

Platform Operations:
- Authenticating users and managing accounts
- Providing customer support and technical assistance
- Improving our services and user experience
- Conducting research and analytics
- Ensuring platform security and preventing fraud

Communication:
- Sending appointment reminders and notifications
- Providing health education and wellness information
- Sharing important updates about our services
- Responding to inquiries and support requests`,
                lastUpdated: '2025-09-05'
            },
            {
                id: 'data-sharing',
                title: 'Information Sharing and Disclosure',
                content: `We may share your information in the following circumstances:

Healthcare Providers:
- With your healthcare team and treating physicians
- With specialists and other medical professionals involved in your care
- With pharmacies and laboratories for treatment purposes
- With emergency responders in case of medical emergencies

Service Providers:
- With third-party vendors who assist in platform operations
- With cloud storage providers for data hosting
- With payment processors for billing and transactions
- With analytics providers for service improvement

Legal Requirements:
- When required by law or legal process
- To protect our rights and prevent fraud
- In case of medical emergencies or public health concerns
- To comply with regulatory requirements

We do not sell, rent, or trade your personal information to third parties for marketing purposes.`,
                lastUpdated: '2025-09-05'
            },
            {
                id: 'data-security',
                title: 'Data Security and Protection',
                content: `We implement comprehensive security measures to protect your information:

Technical Safeguards:
- Encryption of data in transit and at rest
- Secure authentication and access controls
- Regular security audits and vulnerability assessments
- Network security and firewall protection
- Secure backup and disaster recovery systems

Administrative Safeguards:
- Employee training on privacy and security
- Access controls and user authentication
- Regular security policy reviews and updates
- Incident response and breach notification procedures
- Compliance with healthcare privacy regulations

Physical Safeguards:
- Secure data centers with restricted access
- Environmental controls and monitoring
- Secure disposal of physical media
- Protection against unauthorized access

Despite our security measures, no method of transmission over the internet or electronic storage is 100% secure. We cannot guarantee absolute security but are committed to protecting your information.`,
                lastUpdated: '2025-09-05'
            },
            {
                id: 'your-rights',
                title: 'Your Rights and Choices',
                content: `You have certain rights regarding your personal information:

Access and Portability:
- Request access to your personal information
- Obtain copies of your health records
- Transfer your data to another healthcare provider
- View your account information and settings

Correction and Updates:
- Correct inaccurate or incomplete information
- Update your contact and demographic information
- Modify your communication preferences
- Request corrections to your health records

Deletion and Restriction:
- Request deletion of your account and data
- Restrict certain uses of your information
- Opt out of non-essential communications
- Withdraw consent for data processing

To exercise these rights, please contact our Privacy Officer or use the account settings in your dashboard.`,
                lastUpdated: '2025-09-05'
            },
            {
                id: 'compliance',
                title: 'Regulatory Compliance',
                content: `We comply with applicable healthcare privacy regulations:

HIPAA Compliance:
- Health Insurance Portability and Accountability Act
- Administrative, physical, and technical safeguards
- Business Associate Agreements with service providers
- Patient rights and breach notification procedures

Other Regulations:
- General Data Protection Regulation (GDPR)
- State healthcare privacy laws
- Professional licensing requirements
- Industry security standards

We regularly review and update our practices to ensure ongoing compliance with evolving regulations and best practices in healthcare data protection.`,
                lastUpdated: '2025-09-05'
            },
            {
                id: 'contact',
                title: 'Contact Information',
                content: `If you have questions about this Privacy Policy or our data practices, please contact us:

Privacy Officer:
Email: privacy@uzimahealthcare.com
Phone: 1-800-UZIMA-01
Address: 123 Healthcare Drive, Medical City, MC 12345

Data Protection Officer:
Email: dpo@uzimahealthcare.com
Phone: 1-800-UZIMA-02

For privacy-related complaints or concerns:
Email: privacy-complaints@uzimahealthcare.com
Phone: 1-800-UZIMA-03

We will respond to all inquiries within 30 days and work to resolve any concerns promptly and effectively.`,
                lastUpdated: '2025-09-05'
            }
        ];
        setSections(mockSections);
    };

    const filteredSections = sections.filter(section => 
        section.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        section.content.toLowerCase().includes(searchQuery.toLowerCase())
    );

    const currentSection = sections.find(section => section.id === selectedSection);

    return (
        <>
            <DashboardHeader />
            <SideMenu />
            <div className="vertical-overlay" />
            
            <div className="main-content">
                <div className="page-content">
                    <div className="container-fluid">
                        {/* Header */}
                        <div className="row mb-4">
                            <div className="col-12">
                                <div className="d-flex align-items-center justify-content-between">
                                    <div>
                                        <h2 className="fw-bold mb-1">
                                            <FontAwesomeIcon icon={faShieldAlt} className="me-2 text-primary" />
                                            Privacy Policy
                                        </h2>
                                        <p className="text-muted mb-0">
                                            How we protect and handle your data at {clinicName}
                                        </p>
                                    </div>
                                    <div className="text-end">
                                        <div className="badge bg-success fs-6 px-3 py-2">
                                            <FontAwesomeIcon icon={faLock} className="me-1" />
                                            HIPAA Compliant
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Search */}
                        <div className="row mb-4">
                            <div className="col-lg-6">
                                <div className="search-box">
                                    <div className="position-relative">
                                        <input
                                            type="text"
                                            className="form-control"
                                            placeholder="Search privacy policy..."
                                            value={searchQuery}
                                            onChange={(e) => setSearchQuery(e.target.value)}
                                        />
                                        <FontAwesomeIcon 
                                            icon={faSearch} 
                                            className="search-icon position-absolute top-50 end-0 translate-middle-y me-3 text-muted"
                                        />
                                    </div>
                                </div>
                            </div>
                            <div className="col-lg-6 text-end">
                                <button className="btn btn-outline-primary">
                                    <FontAwesomeIcon icon={faDownload} className="me-2" />
                                    Download PDF
                                </button>
                            </div>
                        </div>

                        <div className="row">
                            {/* Navigation */}
                            <div className="col-lg-3">
                                <div className="card border-0 shadow-sm">
                                    <div className="card-header bg-white border-0">
                                        <h6 className="card-title mb-0">Policy Sections</h6>
                                    </div>
                                    <div className="card-body p-0">
                                        <div className="list-group list-group-flush">
                                            {sections.map((section) => (
                                                <button
                                                    key={section.id}
                                                    className={`list-group-item list-group-item-action border-0 ${
                                                        selectedSection === section.id ? 'active' : ''
                                                    }`}
                                                    onClick={() => setSelectedSection(section.id)}
                                                >
                                                    <div className="d-flex align-items-center">
                                                        <FontAwesomeIcon 
                                                            icon={faEye} 
                                                            className="me-2 text-muted"
                                                        />
                                                        <span>{section.title}</span>
                                                    </div>
                                                </button>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Content */}
                            <div className="col-lg-9">
                                <div className="card border-0 shadow-sm">
                                    <div className="card-header bg-white border-0">
                                        <div className="d-flex align-items-center justify-content-between">
                                            <h5 className="card-title mb-0">
                                                {currentSection?.title}
                                            </h5>
                                            <small className="text-muted">
                                                Last updated: {currentSection?.lastUpdated}
                                            </small>
                                        </div>
                                    </div>
                                    <div className="card-body">
                                        <div className="privacy-content">
                                            {currentSection ? (
                                                <div>
                                                    {currentSection.content.split('\n\n').map((paragraph, index) => (
                                                        <p key={index} className="mb-3">
                                                            {paragraph}
                                                        </p>
                                                    ))}
                                                </div>
                                            ) : (
                                                <div className="text-center py-5">
                                                    <FontAwesomeIcon icon={faShieldAlt} className="text-muted fs-1 mb-3" />
                                                    <h5 className="text-muted">Select a section to view</h5>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Key Points */}
                        <div className="row mt-4">
                            <div className="col-12">
                                <div className="card border-0 bg-light">
                                    <div className="card-body">
                                        <h5 className="mb-3">Key Privacy Points</h5>
                                        <div className="row">
                                            <div className="col-md-3 text-center mb-3">
                                                <FontAwesomeIcon icon={faLock} className="text-primary fs-1 mb-2" />
                                                <h6>Secure Data</h6>
                                                <p className="text-muted small">End-to-end encryption and secure storage</p>
                                            </div>
                                            <div className="col-md-3 text-center mb-3">
                                                <FontAwesomeIcon icon={faUser} className="text-success fs-1 mb-2" />
                                                <h6>Your Control</h6>
                                                <p className="text-muted small">Access, update, or delete your data</p>
                                            </div>
                                            <div className="col-md-3 text-center mb-3">
                                                <FontAwesomeIcon icon={faDatabase} className="text-info fs-1 mb-2" />
                                                <h6>HIPAA Compliant</h6>
                                                <p className="text-muted small">Meets healthcare privacy standards</p>
                                            </div>
                                            <div className="col-md-3 text-center mb-3">
                                                <FontAwesomeIcon icon={faGlobe} className="text-warning fs-1 mb-2" />
                                                <h6>Transparent</h6>
                                                <p className="text-muted small">Clear policies and regular updates</p>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            
            <DashboardFooter />
        </>
    );
};

export default PrivacyPolicy;
